import {
  type DefineQuestionnaireDefinitionInput,
  defineQuestionnaireDefinitionInputSchema,
  parseInput,
} from "@repo/api/schemas";
import type { ZodError } from "zod";

type SaveInput = Omit<DefineQuestionnaireDefinitionInput, "definitionUrl">;

const saveInputSchema = defineQuestionnaireDefinitionInputSchema.omit({ definitionUrl: true });

/** The shell's title and description as intent input; a blank description is absent, not "". */
export function questionnaireShellInput(shell: { title: string; description: string }): {
  name: string;
  description?: string;
} {
  const description = shell.description.trim();
  return { name: shell.title.trim(), ...(description ? { description } : {}) };
}

/**
 * Why the intent would refuse this questionnaire, in words the author can act
 * on, or null. Checked before saving so a refusal is shown rather than thrown
 * from the optimistic insert and swallowed.
 */
export function questionnaireSaveProblem(input: SaveInput): string | null {
  try {
    parseInput(saveInputSchema, input);
    return null;
  } catch (error) {
    // By name: the schema's zod is @repo/api's copy, so instanceof can miss.
    if (!(error instanceof Error) || error.name !== "ZodError") throw error;
    const issue = (error as ZodError).issues[0];
    if (!issue) return null;
    return describe(input, issue.path.map(String), issue.code === "too_small") ?? issue.message;
  }
}

function describe(input: SaveInput, path: string[], tooSmall: boolean): string | undefined {
  const last = path.at(-1);
  if (path[0] === "name") return "Give the questionnaire a title.";
  if (path[0] === "scores" || path[0] === "totalBands") {
    if (path.includes("bands") || path[0] === "totalBands") {
      if (last === "label") return "Every score band needs a label.";
      if (last === "from") return "Every score band needs a whole-number start.";
      if (last === "to") return "A score band's end must be a whole number when provided.";
      return undefined;
    }
    if (last === "items") return "Every score needs at least one question.";
    return "Every score needs a name.";
  }
  if (path[0] !== "sections") return undefined;
  if (path.length === 1 && tooSmall) return "Add at least one question.";
  if (path.length === 3 && last === "label") return "Every section needs a title.";
  if (path.length === 3 && last === "items") return "Every section needs at least one question.";
  if (last === "help") return "Question help must contain text when provided.";
  if (last === "detail") return "Option detail must contain text when provided.";
  if (path.includes("options"))
    return last === "options" ? "Every choice needs at least one option." : "Every option needs a label.";
  if (last === "items") return "Every group needs at least one question.";
  if (last === "label") {
    const item = input.sections[Number(path[1])]?.items[Number(path[3])];
    return path.length === 5 && item?.kind === "RepeatingGroup"
      ? "Every repeating group needs a label."
      : "Every question needs a label.";
  }
  if (last === "text") return "Every text block needs some text.";
  return undefined;
}
